import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import LiquidGlassCard from '../../components/LiquidGlassCard/LiquidGlassCard';
import Button from '../../components/Button/Button';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth');
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchUserData = async () => {
      try {
        const response = await api.get('/v1/users', true);
        setUserData(response.data);
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-loading">
            <div className="profile-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <LiquidGlassCard className="profile-error-card">
            <span className="material-symbols-outlined error-icon">error</span>
            <h2>Error Loading Profile</h2>
            <p>{error}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </LiquidGlassCard>
        </div>
      </div>
    );
  }

  const displayUser = userData?.user || user;

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="profile-title">My Profile</h1>
          <p className="profile-subtitle">Manage your account settings and view your information</p>
        </div>

        <div className="profile-content">
          {/* User Info Card */}
          <LiquidGlassCard className="profile-card user-info-card">
            <div className="card-header">
              <h2 className="card-title">Account Information</h2>
            </div>
            <div className="user-avatar-section">
              {displayUser?.avatarUrl ? (
                <img
                  src={displayUser.avatarUrl}
                  alt="Profile"
                  className="user-avatar"
                />
              ) : (
                <div className="user-avatar-placeholder">
                  <span className="material-symbols-outlined">person</span>
                </div>
              )}
              <div className="user-name-section">
                <h3 className="user-name">
                  {displayUser?.firstName || displayUser?.lastName
                    ? `${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim()
                    : displayUser?.username || 'User'}
                </h3>
                <p className="user-email">{displayUser?.email || 'No email'}</p>
              </div>
            </div>

            <div className="user-details">
              <div className="detail-row">
                <span className="detail-label">
                  <span className="material-symbols-outlined">person</span>
                  Username
                </span>
                <span className="detail-value">{displayUser?.username || 'N/A'}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  <span className="material-symbols-outlined">mail</span>
                  Email
                </span>
                <span className="detail-value">{displayUser?.email || 'N/A'}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  <span className="material-symbols-outlined">badge</span>
                  First Name
                </span>
                <span className="detail-value">{displayUser?.firstName || 'N/A'}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  <span className="material-symbols-outlined">badge</span>
                  Last Name
                </span>
                <span className="detail-value">{displayUser?.lastName || 'N/A'}</span>
              </div>
            </div>
          </LiquidGlassCard>

          {/* Balance Card */}
          <LiquidGlassCard className="profile-card balance-card">
            <div className="card-header">
              <h2 className="card-title">Account Balance</h2>
            </div>
            <div className="balance-display">
              <span className="balance-currency">$</span>
              <span className="balance-amount">
                {displayUser?.coins !== undefined
                  ? parseFloat(displayUser.coins).toFixed(2)
                  : '0.00'}
              </span>
            </div>
            {displayUser?.frozenCoins > 0 && (
              <div className="frozen-balance">
                <span className="material-symbols-outlined">ac_unit</span>
                <span className="frozen-label">Frozen:</span>
                <span className="frozen-amount">
                  ${parseFloat(displayUser.frozenCoins).toFixed(2)}
                </span>
              </div>
            )}
            <Button variant="primary" fullWidth onClick={() => navigate('/checkout')}>
              <span className="material-symbols-outlined">add</span>
              Add Funds
            </Button>
            <button className="crypto-funds-btn" onClick={() => navigate('/crypto-payment')}>
              <span className="crypto-funds-icon">₿</span>
              <span>Pay with Crypto</span>
            </button>
          </LiquidGlassCard>

          {/* Actions Card */}
          <LiquidGlassCard className="profile-card actions-card">
            <div className="card-header">
              <h2 className="card-title">Account Actions</h2>
            </div>
            <div className="actions-list">
              <button className="action-item" onClick={() => navigate('/services')}>
                <span className="material-symbols-outlined">sms</span>
                <span>Get New Number</span>
                <span className="material-symbols-outlined chevron">chevron_right</span>
              </button>
              <button className="action-item" onClick={() => navigate('/history')}>
                <span className="material-symbols-outlined">history</span>
                <span>Rental History</span>
                <span className="material-symbols-outlined chevron">chevron_right</span>
              </button>
              <button className="action-item logout" onClick={handleLogout}>
                <span className="material-symbols-outlined">logout</span>
                <span>Sign Out</span>
                <span className="material-symbols-outlined chevron">chevron_right</span>
              </button>
            </div>
          </LiquidGlassCard>
        </div>
      </div>
    </div>
  );
};

export default Profile;
